<template>
  <div class="camps-ada" role="region" aria-label="Camp listings">
    <div class="layer-page">
      <div>
        <div v-if="siteLoading || translateLoading" class="clip-loader camps-ada__loader" role="status" aria-live="polite" aria-label="Loading">
          <clip-loader :color="'#2e8be8'" size="50px" class="clip-loader"></clip-loader>
        </div>
        <div class="langSelectionSection" v-if="!siteLoading && !dataLoading">
          <label class="excludeTranslation" for="camps-ada-lang">Select Language</label>
          <select id="camps-ada-lang" name="langSelector" v-model="selectedLang" @change="langChange($event)" aria-label="Select language">
            <option class="excludeTranslation" value="en">English</option>
            <option class="excludeTranslation" value="es">Spanish</option>
            <option class="excludeTranslation" value="am">Amharic</option>
            <option class="excludeTranslation" value="ar">Arabic</option>
            <option class="excludeTranslation" value="mn">Mongolian</option>
          </select>
        </div>
        <div class="topParagraphText camps-ada__content" role="article" aria-label="Camp information">
          <div v-if="paragraphLoaded" v-html="siteText"></div>
        </div>
        <br/>

        <br/>
        <div v-if="translatedSection" class="topParagraphText translatedSection camps-ada__content" role="article" aria-label="Camp information" v-html="translatedSection"></div>
        <div v-if="!translatedSection" class="fixedElement camps-ada__filters" style="background-color: #f3f3f3;" role="search" aria-label="Filter camps">
          <div class="camps-ada__filter-row">
            <div class="camps-ada__filter-group">
              <label for="camps-ada-filter-title">Title</label>
              <input id="camps-ada-filter-title" type="text" size="20" v-model="fTitle" placeholder="Search by Camp Title" aria-label="Search by camp title">
            </div>
            <div class="camps-ada__filter-group">
              <label for="camps-ada-filter-category">Category</label>
              <select v-model="fCategory" id="camps-ada-filter-category" aria-label="Filter by category">
                <option v-bind:value="'0'">--All--</option>
                <option v-for="category in categories" v-bind:key="category" v-bind:value="category">{{category}}</option>
              </select>
            </div>
            <div class="camps-ada__filter-group">
              <label for="camps-ada-filter-location">Location</label>
              <select v-model="fLocation" id="camps-ada-filter-location" aria-label="Filter by location">
                <option v-bind:value="'0'">--All--</option>
                <option v-for="location in locations" v-bind:key="location" v-bind:value="location">{{location}}</option>
              </select>
            </div>
            <div class="camps-ada__filter-group">
              <label for="camps-ada-filter-age">Age</label>
              <select v-model="fAge" id="camps-ada-filter-age" aria-label="Filter by age range">
                <option v-bind:value="'0'">--All--</option>
                <option v-for="age in ageRanges" v-bind:key="age" v-bind:value="age">{{age}}</option>
              </select>
            </div>
            <div class="camps-ada__filter-group">
              <label for="camps-ada-filter-date">Start Date</label>
              <select v-model="fDate" id="camps-ada-filter-date" aria-label="Filter by start date">
                <option v-bind:value="'0'">--All--</option>
                <option v-for="startDate in startDates" v-bind:key="startDate" v-bind:value="startDate">{{startDate}}</option>
              </select>
            </div>
            <div class="camps-ada__filter-group">
              <label for="camps-ada-filter-availability">Availability</label>
              <select v-model="fWaitList" id="camps-ada-filter-availability" aria-label="Filter by availability">
                <option v-for="wait in waitList" v-bind:key="wait" v-bind:value="wait">{{wait}}</option>
              </select>
            </div>
            <div class="camps-ada__filter-group camps-ada__filter-group--reset">
              <button type="button" class="camps-ada__btn" v-on:click="clearFilters()" aria-label="Reset all filters">Reset</button>
            </div>
          </div>
        </div>
        <br/>

        <div v-if="translateLoading" class="clip-loader camps-ada__loader" role="status" aria-live="polite" aria-label="Loading">
          <clip-loader :color="'#2e8be8'" size="50px" class="clip-loader"></clip-loader>
        </div>

        <!-- WCAG 4.1.3: live region for results count -->
        <p v-if="!siteLoading && !dataLoading && !translateLoading" class="camps-ada__sr-only" role="status" aria-live="polite" aria-atomic="true">{{ resultsStatusText }}</p>

        <!-- WCAG 2.4.11 Focus not obscured: section has scroll-margin in CSS -->
        <section class="camps-ada__table-section" aria-label="Camp listings table">
          <v-client-table ref="campTable" :data="filteredCamps" :columns="columns" :options="options" aria-label="Camp listings table">
          <div slot="Official Title" slot-scope="props" class="camp-title">
            <template v-if="props.row['Registration Website'] && props.row['Registration Website'].length > 7">
              <a class="camps-ada__link" target="_blank" rel="noopener noreferrer" :href="props.row['Registration Website']" :aria-label="props.row['Official Title'] + ' (opens in new window)'">{{ props.row['Official Title'] }}</a>
            </template>
            <span v-else>{{ props.row['Official Title'] }}</span>
          </div>
          <div slot="Activity Code" slot-scope="props" class="camp-activitycode">{{ props.row['activityCode'] }}</div>
          <div slot="Camp Description" slot-scope="props" class="camp-description">{{ props.row['Camp Description'] }}</div>
          <div slot="Provider" slot-scope="props">
            <span v-if="props.row['Camp Type'] === 'DPR'" class="internal" v-tooltip.bottom="'This is an internal program'" aria-label="DPR - This is an internal program" title="This is an internal program">{{ props.row['Camp Type'] }}</span>
            <span v-if="props.row['Camp Type'] !== 'DPR'">{{ props.row['Camp Type'] }}</span>
          </div>
          <div slot="Core Fee" slot-scope="props">
            <span v-if="props.row['Core Fee']" :aria-label="'Core fee: ' + props.row['Core Fee'] + ' dollars'">${{ props.row['Core Fee'] }}</span>
            <span v-else aria-label="No core fee">—</span>
          </div>
          <div slot="Category" slot-scope="props">
            <span v-if="props.row['Category']">{{ props.row['Translated Category'] || props.row['Category'] }}</span>
          </div>
          <div slot="Enrolled" slot-scope="props">
            <span>{{ !props.row['overallEnrollmentCnt'] ? 'n/a' : props.row['overallEnrollmentCnt'] }}</span>
          </div>
          <div slot="Wait List" slot-scope="props">
            <span>{{ props.row['waitListCnt'] }}</span>
          </div>
          <div slot="Registration Website" slot-scope="props">
            <a v-if="props.row['Registration Website'] && props.row['Registration Website'].length > 7" class="camps-ada__link" target="_blank" rel="noopener noreferrer" :href="props.row['Registration Website']" aria-label="Registration website (opens in new window)">Website</a>
            <span v-else aria-hidden="true">—</span>
          </div>
        </v-client-table>
        </section>

        <p v-if="!siteLoading && !dataLoading && filteredCamps.length === 0 && camps.length > 0" class="camps-ada__no-results" role="status" aria-live="polite">
          No camps match your filters. Try changing your search or reset the filters.
        </p>

      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Vue from 'vue'
import { mapGetters, mapActions } from 'vuex'
import VTooltip from 'v-tooltip'
import { ClientTable } from 'vue-tables-2'
import _ from 'lodash'
import { GoogleTranslator } from '@translate-tools/core/translators/GoogleTranslator'
import ClipLoader from 'vue-spinner/src/ClipLoader.vue'

Vue.use(VTooltip)
Vue.use(ClientTable)

const translator = new GoogleTranslator()

export default {
  name: 'Camps',
  components: {
		"clip-loader": ClipLoader
	},
  data () {
    return {
      fTitle: '',
      fCategory: '0',
      fLocation: '0',
      fAge: '0',
      fDate: '0',
      fWaitList: 'Any',
      availableNowInd: true,
      tooltip: 'this is an internal',
      engParagraphText: null,
      paragraphText: null,
      selectedLang: 'en',
      engFixedElementSection: null,
      engLabels: null,
      engOptions: null,
      translatedSection: null,

      columns: ['Official Title', 'Activity Code', 'Camp Description', 'Age Range', 'Location', 'Provider', 'Core Fee', 'Category', 'Date Range', 'Core Camp Time', 'Extended Hours', 'Enrolled', 'Wait List'],
      options: {
        headings: {
          'Official Title': 'Camp Title (Click to Register)'
        },
        sortable: ['Official Title'],
        perPage: 10,
        perPageValues: [10, 100, 5000],
        filterable: false
      },
      waitList: ['---All---','Available Now','Short Waitlist', 'Long Waitlist', 'External Provider']
    }
  },
  mounted: function () {
    this.getCamps()
    this.getSiteText()
    if (Object.keys(this.$route.query).length !== 0) {
      var self = this
      this.selectedLang = self.$route.query.lang
      setTimeout(() => {
        let v = {
          target: {
            value: self.$route.query.lang
          }
        }
        self.langChange(v)
      }, 2000);
    }
    setTimeout(() => {
      this.$refs.campTable.setLoadingState(true)
    }, 1500);
    VTooltip.options.disposeTimeout = 100
  },
  methods: {
    ...mapActions({
      getCamps: 'getCamps',
      translateCamps: 'translateCamps',
      getSiteText: 'getSiteText'
    }),
    clearFilters: function () {
      this.fTitle = ''
      this.fCategory = '0'
      this.fLocation = '0'
      this.fSession = '0'
      this.fAge = '0'
      this.fDate = '0',
      this.availableNowInd = true,
      this.fWaitList= '---All---'
    },
    async setPageText(selector, item, lang ) {
      let selectorHTML = document.querySelectorAll(`${selector}:not(.excludeTranslation)`)
      let innerText = Array.from(selectorHTML, e => e.innerText)
      if(!this[`eng${item}`]) {
        this[`eng${item}`] = innerText
      }
      let translatedText = this[`eng${item}`]
      if(lang) {
        translatedText = await translator.translateBatch(this[`eng${item}`], 'en', lang)
      }
      for (let i = 0; i < selectorHTML.length; i++) {
        selectorHTML[i].innerText = translatedText[i]
      }
    },
    async translateTextOnPage(lang) {
      if(lang == 'en') {
        this.setPageText('label', 'Labels')
        this.setPageText('option', 'Options')
      }
      else {
        this.setPageText('label', 'Labels', lang)
        this.setPageText('option', 'Options', lang)
      }
    },
    langChange(val) {
      const lang = val.target.value;
      if (document.documentElement) {
        document.documentElement.setAttribute('lang', lang);
      }
      this.translateTextOnPage(lang);
      this.translateCamps(lang);
    }
  },
  computed: {
    ...mapGetters(['camps', 'campTypes', 'ageRanges', 'locations', 'categories', 'startDates', 'siteText', 'siteLoading', 'dataLoading', 'translateLoading']),
    resultsStatusText() {
      if (this.siteLoading || this.dataLoading || this.translateLoading) return '';
      const n = this.filteredCamps.length;
      return n === 0 && this.camps.length > 0
        ? 'No camps match your filters.'
        : `Showing ${n} camp${n !== 1 ? 's' : ''}.`;
    },
    paragraphLoaded() {
      if (this.siteText) {

        // load initial paragraph for querying later
        if(this.engParagraphText == null) {
          let paragraphTextPos = this.siteText.indexOf("paragraphText")
          let paragraphTextStartPos = this.siteText.indexOf('>', paragraphTextPos) + 1
          let paragraphTextEndPos = this.siteText.indexOf('<', paragraphTextStartPos)
          let initialParagraph = this.siteText.substring(paragraphTextStartPos, paragraphTextEndPos)
          this.engParagraphText = initialParagraph
          this.paragraphText = initialParagraph
        }
      }
      return this.siteText != null
    },
    filteredCamps: function () {

      let filterData = this.camps

      filterData = this.fDate !== '0' ? _.filter(filterData, (item) => {
        return _.startsWith(item['Date Range'], this.fDate)
      }) : filterData

      filterData = this.fCategory !== '0' ? _.filter(filterData, {'Category': this.fCategory}) : filterData
      filterData = this.fLocation !== '0' ? _.filter(filterData, {'Location': this.fLocation}) : filterData

      filterData = this.fAge !== '0' ? _.filter(filterData, (item) => {
        let startAge = parseInt(item['Age Range'].substring(0, item['Age Range'].indexOf('-')))
        let endAge = parseInt(item['Age Range'].substring(item['Age Range'].indexOf('-') + 1))
        if (this.fAge === 'All Ages') {
          return item['Age Range'] === 'All Ages'
        } else {
          return startAge <= this.fAge && endAge >= this.fAge
        }
      }) : filterData

      let _title = this.fTitle.length > 0 ? this.fTitle.toLocaleLowerCase() : null

      filterData = _title ? filterData.filter((camps) => camps['Official Title'].toLocaleLowerCase().indexOf(_title) !== -1) : filterData

  //    filterData = this.availableNowInd ? filterData.filter(camps => camps['availableNowInd'] == true) : filterData.filter(camps => camps['availableNowInd'] == false)

      filterData = this.fWaitList == '--All--' ? filterData : ((this.fWaitList == 'Long Waitlist' ? filterData.filter(camps => camps['longWaitlistInd'] == true) : this.fWaitList == 'Short Waitlist' ? filterData.filter(camps => camps['shortWaitlistInd'] == true): this.fWaitList == 'Available Now' ? filterData.filter(camps => camps['availableNowInd'] == true): this.fWaitList == 'External Provider'? filterData.filter(camps => !camps['inRectracInd']) : filterData))
      var table = this.$refs.campTable
      if (table) {
        table.setPage(1)
      }

      return filterData
    }
  }
}
</script>

<style lang="scss">

/* WCAG 2.2 AA: .camps-ada prefix overrides genesis-arlington-child.css */
  .camps-ada button:focus-visible,
  .camps-ada a:focus-visible,
  .camps-ada select:focus-visible,
  .camps-ada input:focus-visible {
    outline: 2px solid #005cbb !important;
    outline-offset: 2px !important;
  }
  .camps-ada .camps-ada__table-section,
  .camps-ada .camps-ada__filters {
    scroll-margin-top: 80px;
  }
  .camps-ada .camps-ada__btn,
  .camps-ada select,
  .camps-ada input[type="text"] {
    min-height: 24px !important;
    min-width: 24px !important;
  }
  .camps-ada .camps-ada__link {
    min-height: 24px;
    min-width: 24px;
    padding: 4px 0;
    display: inline-flex;
    align-items: center;
  }
  .camps-ada nav a {
    min-height: 24px !important;
    min-width: 24px !important;
    padding: 4px 8px !important;
    display: inline-flex !important;
    align-items: center !important;
  }
  .camps-ada .internal {
    color: #b71c1c !important;
    font-weight: bold;
  }
  /* WCAG 1.4.3: Override genesis #E38410 so Strapi headings (e.g. "2026 Summer Camp Info", "Frequently Asked Questions") pass contrast */
  .camps-ada .topParagraphText h2,
  .camps-ada .topParagraphText h3,
  .camps-ada .topParagraphText h4,
  .camps-ada .topParagraphText h5,
  .camps-ada .topParagraphText h6,
  .camps-ada .translatedSection h2,
  .camps-ada .translatedSection h3,
  .camps-ada .translatedSection h4,
  .camps-ada .translatedSection h5,
  .camps-ada .translatedSection h6 {
    color: #D77D0F !important;
  }
  .camps-ada .camp-description {
    min-height: 50px;
    max-height: 150px;
    height: auto;
    overflow-y: auto;
    overflow-x: hidden;
  }
  /* WCAG 1.4.3: darker blue so white text passes contrast (4.5:1); #2e8be8 fails */
  .camps-ada tr th {
    background-color: #005cb8;
    color: #fff;
    font-size: 1.5em;
    font-weight: 700;
    padding: 5px;
  }
  .camps-ada tr:first-child {
    background-color: #ebebeb;
    color: #000;
  }
  .camps-ada tr:first-child td {
    font-weight: normal;
    font-size: 1.3em;
  }
  .camps-ada td:nth-child(3) {
    min-height: 50px;
    max-height: 75px;
    max-width: 300px;
    min-width: 300px;
    height: auto;
    overflow-y: auto;
    overflow-x: hidden;
  }
  .camps-ada .langSelectionSection {
    position: absolute;
    top: 55px;
    right: 10%;
  }
  .camps-ada tr th {
    text-align: left;
    padding-left: 10px;
  }
  .camps-ada .topParagraphText ul,
  .camps-ada .translatedSection ul {
    display: block !important;
    list-style-type: disc !important;
    margin: 0.5em 0 0.5em 1.5em !important;
    padding-left: 1em !important;
  }
  .camps-ada .topParagraphText ul li,
  .camps-ada .translatedSection ul li {
    display: list-item !important;
    margin: 0.25em 0 !important;
    list-style-type: inherit !important;
  }
  .camps-ada nav ul {
    overflow: hidden;
    display: flex !important;
    list-style: none !important;
  }
  .camps-ada nav ul li {
    font-size: 1.3em;
    margin-left: 1em;
    padding-left: 1em;
    text-indent: -1em;
    list-style-type: none !important;
  }
  .camps-ada ul.pagination {
    display: flex !important;
    list-style: none !important;
    color: #000;
    justify-content: flex-end;
  }

  .camps-ada .VuePagination__pagination-item {
    margin: 5px;
    text-align: center;
    text-indent: 0;
    padding-left: 0;
  }
  .camps-ada .VuePagination nav ul.pagination li {
    text-indent: 0;
    padding-left: 0;
  }
  .camps-ada .VuePagination__pagination-item a,
  .camps-ada .VuePagination nav .page-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2.75em;
    padding: 6px 12px;
    box-sizing: border-box;
    text-align: center;
  }
  .camps-ada .VuePagination {
    text-align: right;
    width: 1280px;
  }
  .camps-ada .VuePagination__pagination {
    margin-top: 5px;
  }
  .camps-ada nav .page-link {
    color: #000;
  }

  .camps-ada .camps-ada__filter-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-end;
    gap: 12px;
    overflow-x: auto;
    padding-bottom: 4px;
  }
  .camps-ada .camps-ada__filter-group {
    flex: 0 0 auto;
  }
  .camps-ada .camps-ada__filter-group--reset {
    margin-left: auto;
  }

  .camps-ada .camps-ada__filters input[type="text"],
  .camps-ada .camps-ada__filters select,
  .camps-ada .camps-ada__filters .camps-ada__btn {
    height: 30px;
    min-height: 30px;
    box-sizing: border-box;
    padding-top: 6px;
    padding-bottom: 6px;
  }
  .camps-ada .camps-ada__filters .camps-ada__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .camps-ada #camps-ada-filter-title {
    width: 160px;
    min-width: 160px;
  }
  .camps-ada #camps-ada-filter-category,
  .camps-ada #camps-ada-filter-location,
  .camps-ada #camps-ada-filter-age,
  .camps-ada #camps-ada-filter-date,
  .camps-ada #camps-ada-filter-availability {
    width: 190px;
  }
  .camps-ada .camps-ada__filters input,
  .camps-ada .camps-ada__filters label {
    display: block;
  }
  .camps-ada .camps-ada__filters label {
    margin-bottom: 5px;
    font-weight: bold;
    font-size: 1.3em;
    color: #555 !important;
  }
  .camps-ada .tooltip {
    display: block !important;
  }
  .camps-ada .tooltip .tooltip-inner {
    background: black;
    color: white;
    border-radius: 16px;
    padding: 5px 10px 4px;
  }
  .camps-ada .clip-loader,
  .camps-ada .camps-ada__loader {
    left: 50%;
    right: 50%;
    bottom: 60%;
    top: 40%;
  }
  .camps-ada .camps-ada__sr-only {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
  }
  .camps-ada .camps-ada__no-results {
    margin: 1rem 0;
    padding: 1rem;
    background: #f3f3f3;
    font-size: 1.1em;
  }
</style>



// WEBPACK FOOTER //
// src/components/camps/camps.vue